import React, { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Loading } from '../../ui/components';
import { LoggerService, getAccessToken, getEnvironment, isTokenValid } from '../../ui/utils';
import { AUTH_ADMIN_CLIENT_IDS } from '../../envVars';
import { useAPICall, useAppDispatch } from '../../hooks';
import authService from '../../sdk/services/authService';
import { fetchLoginUser } from '../../sdk/store/authActions';

interface RequiredAuthProps {
  children?: JSX.Element;
}

export const RequiredAuth: React.FC<RequiredAuthProps> = ({ children }: RequiredAuthProps) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const apiCall = useAPICall();
  const isLocal = useRef(['LOCAL', 'ISSDEV', 'ISSUAT'].includes(getEnvironment())).current;
  const dispatch = useAppDispatch();
  const redirectingRef = useRef(false);

  useEffect(() => {
    if (isLocal) {
      return;
    }

    // Don't do anything if already redirecting
    if (redirectingRef.current) {
      return;
    }

    // Wait for MSAL to finish any in-progress interaction before proceeding
    if (inProgress !== 'none') {
      return;
    }

    const handleAuth = async () => {
      if (!isAuthenticated) {
        // Set flag to prevent multiple redirects
        redirectingRef.current = true;
        
        try {
          instance.loginRedirect({
            scopes: ['openid', 'profile', 'email', `api://${AUTH_ADMIN_CLIENT_IDS}/.default`],
            redirectStartPage: window.location.href,
          });
        } catch (error) {
          console.error('[RequiredAuth] Error during loginRedirect:', error);
          redirectingRef.current = false;
        }
      } else {
        // Reset redirecting flag when authenticated
        redirectingRef.current = false;
        
        // Get access token from MSAL
        try {
          const request = {
            scopes: ['openid', 'profile', 'email', `api://${AUTH_ADMIN_CLIENT_IDS}/.default`],
            account: accounts[0],
          };
          
          const response = await instance.acquireTokenSilent(request);
          const loginToken = response.accessToken;
          
          if (loginToken) {
            localStorage.setItem('loginToken', loginToken);
            fetchAppToken(loginToken);
          } else {
            LoggerService.log('Undefined Access Token');
          }
        } catch (error) {
          LoggerService.log('Error acquiring token:', error);
          // Reset redirecting flag on error too
          redirectingRef.current = false;
        }
      }
    };

    handleAuth();
  }, [instance, isAuthenticated, accounts, inProgress]);

  const fetchAppToken = async (loginToken: string) => {
    let atoken = getAccessToken() || '';
    if (loginToken && !isTokenValid(atoken)) {
      let result = await apiCall(authService.getAppToken);
      if (result.data?.token) {
        dispatch(fetchLoginUser(result.data?.token));
      }
    }
  };

  if (!isLocal && !isAuthenticated) {
    return <Loading />;
  }

  return children ?? <Outlet />;
};